import React, { useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

const VARIANTS = {
  directory: {
    wrapperStyles: ({ theme, $isMobile }) => css`
      text-align: left;
      margin-top: ${$isMobile ? theme.unit(4) : theme.unit(8)};
      margin-bottom: ${theme.unit(4)};
    `,
    buttonMarginRight: ({ theme, $isMobile }) => ($isMobile ? theme.unit(3) : theme.unit(4)),
    listWrap: 'wrap',
    labelDisplay: 'inline-block',
  },
  myZone: {
    wrapperStyles: ({ theme, $isMobile }) => css`
      text-align: left;
      margin-bottom: ${$isMobile ? theme.unit(6) : theme.unit(4)};
    `,
    buttonMarginRight: ({ theme, $isMobile }) => ($isMobile ? theme.unit(3) : theme.unit(4)),
    listWrap: 'nowrap',
    labelDisplay: ({ $isMobile }) => ($isMobile ? 'inline' : 'inline-block'),
  },
};

const Wrapper = styled.div`
  ${({ $variant, ...props }) => VARIANTS[$variant].wrapperStyles(props)}
`;

const List = styled.ul`
  display: flex;
  flex-wrap: ${({ $variant, ...props }) => {
    const wrapSetting = VARIANTS[$variant].listWrap;
    return typeof wrapSetting === 'function' ? wrapSetting(props) : wrapSetting;
  }};
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  margin-top: 0;
`;

const ListItem = styled.li`
  flex: 0 1 auto;
  min-width: 0;
`;

const Button = styled.button`
  font-family: inherit;
  background-color: transparent;
  overflow: visible;
  text-transform: none;
  border: none;
  text-align: left;
  vertical-align: middle;
  user-select: none;
  padding: 0;
  text-decoration: none;
  display: inline-block;
  margin-right: ${({ $variant, ...props }) => VARIANTS[$variant].buttonMarginRight(props)};
  color: ${({ theme }) => theme.c.text.interactive};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.c.text['interactive-hover']};
  }

  &:active {
    color: ${({ theme }) => theme.c.text.interactive};
  }

  &:focus {
    outline: none;
  }
`;

const Label = styled.h2`
  margin: 0;
  text-transform: uppercase;
  ${({ theme }) => theme.typographyRebrand.heading['2xl']}
  display: ${({ $variant, ...props }) => {
    const displaySetting = VARIANTS[$variant].labelDisplay;
    return typeof displaySetting === 'function' ? displaySetting(props) : displaySetting;
  }};
  text-decoration-line: underline;
  text-decoration-color: transparent;
  text-decoration-thickness: 2px;
  text-underline-offset: 2px;

  ${({ $isActive }) => $isActive && css`
    text-decoration-color: inherit;
  `}

  ${Button}:hover & {
    text-decoration-color: inherit;
  }

  ${Button}:active & {
    text-decoration-color: inherit;
  }
`;

const TabGroup = ({
  tabs = [],
  activeTab = '',
  onTabChange = () => {},
  isMobile = false,
  variant = 'directory',
  wrapperRole = 'navigation',
  ariaLabel = 'Tabs',
  focusActiveTabOnChange = true,
  idPrefix = 'tab-group',
}) => {
  const buttonRefs = useRef({});

  const visibleTabs = useMemo(
    () => tabs.filter(({ id, label }) => id && label),
    [tabs],
  );

  useEffect(() => {
    if (!focusActiveTabOnChange) {
      return;
    }

    const activeButton = buttonRefs.current[activeTab];
    if (activeButton && activeButton.focus) {
      activeButton.focus();
    }
  }, [activeTab, focusActiveTabOnChange]);

  const onKeyDown = (event, index) => {
    const { key } = event;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
      return;
    }

    event.preventDefault();

    if (!visibleTabs.length) {
      return;
    }

    let nextIndex = index;
    if (key === 'Home') {
      nextIndex = 0;
    } else if (key === 'End') {
      nextIndex = visibleTabs.length - 1;
    } else if (key === 'ArrowRight') {
      nextIndex = (index + 1) % visibleTabs.length;
    } else if (key === 'ArrowLeft') {
      nextIndex = (index - 1 + visibleTabs.length) % visibleTabs.length;
    }

    onTabChange(visibleTabs[nextIndex].id);
  };

  return (
    <Wrapper role={wrapperRole} $variant={variant} $isMobile={isMobile}>
      <List role="tablist" aria-label={ariaLabel} $variant={variant} $isMobile={isMobile}>
        {visibleTabs.map((tab, index) => {
          const tabId = `${idPrefix}-${tab.id}`;
          const isActive = tab.id === activeTab;

          return (
            <ListItem key={tab.id}>
              <Button
                id={tabId}
                role="tab"
                type="button"
                aria-selected={isActive}
                aria-controls={tab.panelId}
                tabIndex={isActive ? 0 : -1}
                data-track-id={tab.analytics && tab.analytics.dataTrackingId}
                data-track-click={tab.analytics && tab.analytics.dataTrack}
                data-track-type={tab.analytics && tab.analytics.dataTrackType}
                onClick={() => onTabChange(tab.id)}
                onKeyDown={(event) => onKeyDown(event, index)}
                ref={(element) => {
                  buttonRefs.current[tab.id] = element;
                }}
                $variant={variant}
                $isMobile={isMobile}
              >
                <Label $isActive={isActive} $variant={variant} $isMobile={isMobile}>
                  {tab.label}
                </Label>
              </Button>
            </ListItem>
          );
        })}
      </List>
    </Wrapper>
  );
};

export default TabGroup;
